import { useState, useEffect } from 'react'
import { opportunitiesService } from '@/services/opportunities-service'
import type { OpportunityPreferences } from '@/types'

// SAM.gov feed is not offered at launch — hide the toggle + filter card until we ship it.
// Flip to `true` to restore the SAM.gov Feed card (toggle, radius, job types, set-aside filters).
const SAM_FEED_ENABLED = false

const JOB_TYPES = ['Asphalt', 'Concrete', 'Grading', 'Seal Coat', 'Striping', 'ADA', 'General Paving']
const SET_ASIDES = ['All', 'Small Business (SBA)', 'Service-Disabled Veteran (SDVOSB)', '8(a)', 'HUBZone', 'Women-Owned (WOSB)']
const RADIUS_OPTIONS = [25, 50, 75, 100, 150]

export function OpportunityFeedSettings() {
  const [preferences, setPreferences] = useState<OpportunityPreferences | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null)
  const [toggleSaving, setToggleSaving] = useState(false)
  const [toggleStatus, setToggleStatus] = useState<'success' | 'error' | null>(null)

  // Form state
  const [feedEnabled, setFeedEnabled] = useState(false)
  const [samDetailsOpen, setSamDetailsOpen] = useState(true)
  const [searchRadius, setSearchRadius] = useState(50)
  const [jobTypes, setJobTypes] = useState<string[]>([])
  const [minimumBudget, setMinimumBudget] = useState<number | ''>('')
  const [minimumSquareFootage, setMinimumSquareFootage] = useState<number | ''>('')
  const [setAsideFilter, setSetAsideFilter] = useState<string[]>([])
  const [aiInsightsEnabled, setAiInsightsEnabled] = useState(true)

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await opportunitiesService.getPreferences()
        setPreferences(prefs)
        setFeedEnabled(prefs.feedEnabled)
        setSearchRadius(prefs.searchRadiusMiles)
        setJobTypes(prefs.jobTypes ? JSON.parse(prefs.jobTypes) : [])
        setMinimumBudget(prefs.minimumBudget || '')
        setMinimumSquareFootage(prefs.minimumSquareFootage || '')
        setSetAsideFilter(prefs.setAsideFilter ? JSON.parse(prefs.setAsideFilter) : [])
        setAiInsightsEnabled(prefs.aiInsightsEnabled ?? true)
      } catch (error) {
        console.error('Failed to load opportunity preferences:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadPreferences()
  }, [])

  // True only when non-toggle form fields differ from saved values
  const hasChanges = preferences !== null && (
    searchRadius !== preferences.searchRadiusMiles ||
    JSON.stringify(jobTypes) !== (preferences.jobTypes ?? '[]') ||
    (minimumBudget === '' ? null : Number(minimumBudget)) !== (preferences.minimumBudget ?? null) ||
    (minimumSquareFootage === '' ? null : Number(minimumSquareFootage)) !== (preferences.minimumSquareFootage ?? null) ||
    JSON.stringify(setAsideFilter) !== (preferences.setAsideFilter ?? '[]')
  )

  async function handleToggle(field: 'feedEnabled' | 'aiInsightsEnabled', newValue: boolean) {
    if (toggleSaving || !preferences) return
    if (field === 'feedEnabled') {
      setFeedEnabled(newValue)
      if (newValue) setSamDetailsOpen(true)
    } else {
      setAiInsightsEnabled(newValue)
    }

    setToggleSaving(true)
    setToggleStatus(null)
    try {
      const updated = await opportunitiesService.updatePreferences({
        feedEnabled: field === 'feedEnabled' ? newValue : feedEnabled,
        searchRadiusMiles: searchRadius,
        jobTypes: JSON.stringify(jobTypes),
        minimumBudget: minimumBudget ? Number(minimumBudget) : undefined,
        minimumSquareFootage: minimumSquareFootage ? Number(minimumSquareFootage) : undefined,
        setAsideFilter: JSON.stringify(setAsideFilter),
        aiInsightsEnabled: field === 'aiInsightsEnabled' ? newValue : aiInsightsEnabled,
      })
      setPreferences(updated)
      setToggleStatus('success')
      setTimeout(() => setToggleStatus(null), 2000)
    } catch (error) {
      console.error('Failed to save toggle:', error)
      if (field === 'feedEnabled') setFeedEnabled(!newValue)
      else setAiInsightsEnabled(!newValue)
      setToggleStatus('error')
      setTimeout(() => setToggleStatus(null), 3000)
    } finally {
      setToggleSaving(false)
    }
  }

  const handleSave = async () => {
    setSaveStatus(null)
    setIsSaving(true)
    try {
      const updated = await opportunitiesService.updatePreferences({
        feedEnabled,
        searchRadiusMiles: searchRadius,
        jobTypes: JSON.stringify(jobTypes),
        minimumBudget: minimumBudget ? Number(minimumBudget) : undefined,
        minimumSquareFootage: minimumSquareFootage ? Number(minimumSquareFootage) : undefined,
        setAsideFilter: JSON.stringify(setAsideFilter),
        aiInsightsEnabled,
      })
      setPreferences(updated)
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Failed to save preferences:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const toggleJobType = (jobType: string) => {
    setJobTypes(prev => prev.includes(jobType) ? prev.filter(jt => jt !== jobType) : [...prev, jobType])
  }

  const toggleSetAside = (setAside: string) => {
    setSetAsideFilter(prev => prev.includes(setAside) ? prev.filter(sa => sa !== setAside) : [...prev, setAside])
  }

  if (isLoading) {
    return (
      <div className="opp-feed-section">
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading preferences...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {toggleStatus && (
        <div style={{ fontSize: '13px', fontWeight: 500, color: toggleStatus === 'success' ? '#16a34a' : '#dc2626' }}>
          {toggleStatus === 'success' ? 'Saved' : 'Failed to save — try again'}
        </div>
      )}

      {/* AI Insights — simple toggle row, no feed-specific filters */}
      <div className="opp-feed-toggle-row">
        <div className="opp-feed-toggle-text">
          <h3 className="opp-feed-label">AI Insights</h3>
          <p className="opp-feed-desc">Automatically analyze new opportunities with AI to estimate costs, confidence scores, and scope of work</p>
        </div>
        <button
          className={`opp-feed-toggle ${aiInsightsEnabled ? 'active' : ''}`}
          onClick={() => handleToggle('aiInsightsEnabled', !aiInsightsEnabled)}
          disabled={toggleSaving}
        >
          <div className="opp-feed-toggle-thumb"></div>
        </button>
      </div>

      {/* SAM.gov Feed — card with collapsible details. Hidden until the feed ships (see SAM_FEED_ENABLED). */}
      {SAM_FEED_ENABLED && (
      <div className="opp-feed-card">
        <div className="opp-feed-card-header">
          <button
            className={`opp-feed-toggle ${feedEnabled ? 'active' : ''}`}
            onClick={() => handleToggle('feedEnabled', !feedEnabled)}
            disabled={toggleSaving}
          >
            <div className="opp-feed-toggle-thumb"></div>
          </button>
          <div className="opp-feed-card-title">
            <h3 className="opp-feed-label" style={{ marginBottom: 0 }}>SAM.gov Feed</h3>
            <p className="opp-feed-desc" style={{ marginBottom: 0 }}>Automatically pull paving opportunities from the federal government's SAM.gov database</p>
          </div>
          {feedEnabled && (
            <button
              className="opp-feed-card-chevron"
              onClick={() => setSamDetailsOpen(o => !o)}
              aria-label={samDetailsOpen ? 'Collapse details' : 'Expand details'}
            >
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: samDetailsOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
          )}
        </div>

        {feedEnabled && samDetailsOpen && (
          <div className="opp-feed-card-body">
            <div className="opp-feed-section">
              <h3 className="opp-feed-label">Search Radius</h3>
              <p className="opp-feed-desc">Only show opportunities within this distance of your company address</p>
              <div className="opp-feed-radius">
                {RADIUS_OPTIONS.map(radius => (
                  <button
                    key={radius}
                    onClick={() => setSearchRadius(radius)}
                    className={`opp-feed-radius-btn ${searchRadius === radius ? 'active' : ''}`}
                  >
                    {radius} miles
                  </button>
                ))}
              </div>
            </div>

            <div className="opp-feed-section">
              <h3 className="opp-feed-label">Job Types</h3>
              <p className="opp-feed-desc">Filter opportunities by the types of work your company performs</p>
              <div className="opp-feed-checkboxes">
                {JOB_TYPES.map(jobType => (
                  <label key={jobType} className="opp-feed-checkbox">
                    <input type="checkbox" checked={jobTypes.includes(jobType)} onChange={() => toggleJobType(jobType)} />
                    {jobType}
                  </label>
                ))}
              </div>
            </div>

            <div className="opp-feed-section">
              <h3 className="opp-feed-label">Minimum Budget (optional)</h3>
              <p className="opp-feed-desc">Hide opportunities with an estimated budget below this amount</p>
              <input
                type="number" className="opp-feed-input" placeholder="e.g., 50000"
                value={minimumBudget} onChange={(e) => setMinimumBudget(e.target.value ? Number(e.target.value) : '')}
                min="0" step="1000"
              />
            </div>

            <div className="opp-feed-section">
              <h3 className="opp-feed-label">Minimum Square Footage (optional)</h3>
              <p className="opp-feed-desc">Hide opportunities smaller than this square footage</p>
              <input
                type="number" className="opp-feed-input" placeholder="e.g., 5000"
                value={minimumSquareFootage} onChange={(e) => setMinimumSquareFootage(e.target.value ? Number(e.target.value) : '')}
                min="0" step="100"
              />
            </div>

            <div className="opp-feed-section">
              <h3 className="opp-feed-label">Set-Aside Preference (optional)</h3>
              <p className="opp-feed-desc">Filter by small business set-aside type</p>
              <div className="opp-feed-checkboxes">
                {SET_ASIDES.map(setAside => (
                  <label key={setAside} className="opp-feed-checkbox">
                    <input type="checkbox" checked={setAsideFilter.includes(setAside)} onChange={() => toggleSetAside(setAside)} />
                    {setAside}
                  </label>
                ))}
              </div>
            </div>

            <div className="opp-feed-card-footer">
              <button className="opp-feed-save-btn" onClick={handleSave} disabled={isSaving || !hasChanges}>
                {isSaving ? 'Saving...' : 'Save Preferences'}
              </button>
              {saveStatus === 'success' && <p className="opp-feed-saved">Saved</p>}
              {saveStatus === 'error' && <p style={{ color: '#dc2626', fontSize: '13px', fontWeight: '500' }}>Failed to save</p>}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}
